import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { motion, useAnimation } from "framer-motion";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

import { Card } from "../../../../components";
import { useTopikMotion } from "../topikMotion";
import { pointInRect } from "./dragPlacement";
import ChosenCardDialog from "../../rounds/pick-candidate/chosen-card-dialog/ChosenCardDialog";

// Q2 - select (§12.2): a colored target box (icon + label + polarity colour) makes the
// development-direction question visually distinct from Q1's neutral scale. A card is chosen by
// *dragging* it into the box or *tapping* it; either way the ChosenCardDialog confirm opens with
// the question still rendered behind it (critique S4). The box is a real drop target, so it lights
// up while a card is dragged over it.
const asDialogCard = (card, polarity) => ({
  id: card.id,
  title: card.title,
  description: card.description || "",
  suit: { kind: polarity },
});

const POLARITY_ICON = { negative: "↓", positive: "↑" };

const TopikOutcomeStep = ({ t, polarity, slot, boxLabel, cardA, cardB, submitting, onConfirm }) => {
  const { reduce, layout, deal } = useTopikMotion();
  const [focusedCard, setFocusedCard] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [overBox, setOverBox] = useState(false);

  const boxRef = useRef(null);
  // Exactly two cards -> two stable animation controls at the top level (no hooks in a loop),
  // used to spring a dropped-but-missed card back to its dealt origin.
  const controlsA = useAnimation();
  const controlsB = useAnimation();
  const controlsFor = (cardId) => (cardId === cardA.id ? controlsA : controlsB);

  // Deal the pair in on mount (staggered), then leave the controls free for drag snap-back.
  useEffect(() => {
    controlsA.start("animate");
    controlsB.start("animate");
  }, [controlsA, controlsB]);

  const focus = (card) => {
    if (!submitting && !focusedCard) {
      setFocusedCard(asDialogCard(card, polarity));
    }
  };

  // True when the live pointer sits over the target box.
  const pointerOverBox = (point) => (
    pointInRect(point, boxRef.current ? boxRef.current.getBoundingClientRect() : null)
  );

  const handleDragEnd = (card, info) => {
    setDragging(false);
    setOverBox(false);
    if (!submitting && !focusedCard && pointerOverBox(info.point)) {
      focus(card);
    } else {
      // Missed the box (or a dialog is already open): spring back to origin.
      controlsFor(card.id).start({ x: 0, y: 0, transition: layout });
    }
  };

  const boxClass = [
    "topik-outcome__box",
    `topik-outcome__box--${polarity}`,
    dragging ? "topik-outcome__box--active" : "",
    overBox ? "topik-outcome__box--over" : "",
  ].filter(Boolean).join(" ");

  return (
    <>
      <div className={`topik-outcome topik-outcome--${polarity}${slot === "final" ? " topik-outcome--final" : ""}`}>
        <div className="topik-view__card-pair">
          {[cardA, cardB].map((card, index) => (
            <motion.div
              key={card.id}
              className="topik-outcome__pickable"
              role="button"
              tabIndex={0}
              aria-label={t("topik-comparison-view.place-card", { card: card.title })}
              custom={index}
              variants={deal}
              initial={reduce ? false : "initial"}
              animate={controlsFor(card.id)}
              transition={layout}
              drag={!submitting}
              dragMomentum={false}
              whileDrag={{ scale: 1.04, zIndex: 30, cursor: "grabbing" }}
              onDragStart={() => setDragging(true)}
              onDrag={(e, info) => {
                const over = pointerOverBox(info.point);
                setOverBox((prev) => (prev === over ? prev : over));
              }}
              onDragEnd={(e, info) => handleDragEnd(card, info)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  focus(card);
                }
              }}
            >
              <Card
                cardId={card.id}
                title={card.title}
                description={card.description || ""}
                suit={polarity}
                onClick={() => focus(card)}
              />
            </motion.div>
          ))}
        </div>

        <div ref={boxRef} className={boxClass}>
          <span className="topik-outcome__box-icon" aria-hidden="true">
            {POLARITY_ICON[polarity]}
          </span>
          <span className="topik-outcome__box-label">{boxLabel}</span>
        </div>
      </div>

      <ChosenCardDialog
        card={focusedCard}
        onDismiss={() => setFocusedCard(null)}
        actions={[
          {
            action: "confirm",
            icon: <PlayCircleIcon />,
            label: t("topik-comparison-view.pick-button"),
            callback: () => {
              const cardId = focusedCard.id;
              setFocusedCard(null);
              onConfirm(cardId);
            },
          },
          {
            action: "cancel",
            label: t("topik-comparison-view.choose-another"),
            buttonStyle: "subtle",
            callback: () => setFocusedCard(null),
          },
        ]}
      />
    </>
  );
};

TopikOutcomeStep.propTypes = {
  t: PropTypes.func.isRequired,
  polarity: PropTypes.oneOf(["negative", "positive"]).isRequired,
  slot: PropTypes.string.isRequired,
  boxLabel: PropTypes.string.isRequired,
  cardA: PropTypes.object.isRequired,
  cardB: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
};

TopikOutcomeStep.defaultProps = {
  submitting: false,
};

export default TopikOutcomeStep;
